// src/store/slices/numberLinesSlice.ts
/**
 * Primary number line configuration with localStorage persistence
 * Implements Section 5.2 of the specification
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NumberLineState, Fraction } from '../../types/redux';

const NUMBER_LINE_STORAGE_KEY = 'nl:numberLine';

// Default number line state
const defaultNumberLineState: NumberLineState = {
  id: 'primary',
  unitInterval: { n: 1, d: 1 }, // integers by default
  tickMode: 'labeled',
  labelPosition: 'below',
  numberType: 'integer',
  fractionDisplay: {
    format: 'mixed',
    simplify: true,
  },
  subTickDivisions: null,
};

/**
 * Load number line state from localStorage
 */
function loadNumberLineFromStorage(): NumberLineState {
  try {
    const stored = localStorage.getItem(NUMBER_LINE_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new property additions
      return { ...defaultNumberLineState, ...parsed };
    }
  } catch (error) {
    console.warn('Failed to load number line from storage:', error);
  }
  return defaultNumberLineState;
}

/**
 * Save number line state to localStorage
 */
function saveNumberLineToStorage(numberLine: NumberLineState): void {
  try {
    localStorage.setItem(NUMBER_LINE_STORAGE_KEY, JSON.stringify(numberLine));
  } catch (error) {
    console.warn('Failed to save number line to storage:', error);
  }
}

const initialState: NumberLineState = loadNumberLineFromStorage();

export const numberLinesSlice = createSlice({
  name: 'numberLines',
  initialState,
  reducers: {
    /**
     * Set the unit interval (spacing between major ticks)
     */
    setUnitInterval: (state, action: PayloadAction<Fraction>) => {
      state.unitInterval = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set tick display mode
     */
    setTickMode: (state, action: PayloadAction<'none' | 'ticks' | 'labeled'>) => {
      state.tickMode = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set label position relative to the line
     */
    setLabelPosition: (state, action: PayloadAction<'below' | 'above'>) => {
      state.labelPosition = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Set number type display
     */
    setNumberType: (state, action: PayloadAction<'integer' | 'decimal' | 'fraction' | 'percent'>) => {
      state.numberType = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Toggle fraction format between mixed and improper
     */
    toggleFractionFormat: (state) => {
      state.fractionDisplay.format = 
        state.fractionDisplay.format === 'mixed' ? 'improper' : 'mixed';
      saveNumberLineToStorage(state);
    },

    /**
     * Toggle fraction simplification
     */
    toggleFractionSimplify: (state) => {
      state.fractionDisplay.simplify = !state.fractionDisplay.simplify;
      saveNumberLineToStorage(state);
    },

    /**
     * Set subtick divisions (null for none)
     */
    setSubTickDivisions: (state, action: PayloadAction<number | null>) => {
      state.subTickDivisions = action.payload;
      saveNumberLineToStorage(state);
    },

    /**
     * Reset to default configuration
     */
    resetNumberLine: (state) => {
      Object.assign(state, defaultNumberLineState);
      saveNumberLineToStorage(state);
    },

    /**
     * Load number line state from shared scene
     */
    loadNumberLineState: (state, action: PayloadAction<NumberLineState>) => {
      Object.assign(state, action.payload);
      saveNumberLineToStorage(state);
    },
  },
});

export const {
  setUnitInterval,
  setTickMode,
  setLabelPosition,
  setNumberType,
  toggleFractionFormat,
  toggleFractionSimplify,
  setSubTickDivisions,
  resetNumberLine,
  loadNumberLineState,
} = numberLinesSlice.actions;

export default numberLinesSlice.reducer;